import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { KanbanBoard } from "../components/KanbanBoard";
import { DataPorter } from "../components/DataPorter";
import { FunnelList, ViewToggle } from "../components/FunnelList";
import { PATIENT_STAGES, ACTIVE_PATIENT_STAGES, CLOSED_PATIENT_STAGE_DEFS, CLOSED_PATIENT_STAGES, PATIENT_STAGE_COLOR, PAYMENT_METHODS, LOST_REASONS, OVERDUE_DAYS, inr, labelOf } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Stethoscope, HeartPulse, Megaphone, Trash2, Receipt as ReceiptIcon, AlertTriangle } from "lucide-react";
import { LostReasonDialog, LostReasonFields } from "../components/LostReasonDialog";
import { DatePickerField } from "../components/DatePicker";
import { PaymentReminder } from "../components/PaymentReminder";
import { MarkPaidPopover, MethodPicker, todayLocal } from "../components/MarkPaidPopover";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const DIRECT_CHANNELS = ["Google Ads", "Instagram", "Referral (friend)", "Walk-in", "Health camp"];
const PAY = { paid: "bg-emerald-100 text-emerald-700", unpaid: "bg-slate-100 text-slate-500", refunded: "bg-rose-100 text-rose-700" };
const empty = { name: "", phone: "", email: "", source_type: "clinician_order", direct_channel: "Google Ads", originating_doctor_id: "", assigned_clinician_id: "", sessions_total: 10, revenue_per_session: 1000 };

export default function PatientsFunnel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [pedit, setPEdit] = useState(null);
  const [listScope, setListScope] = useState("active");
  const [lostFor, setLostFor] = useState(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const overdue = items.filter((p) => p.overdue_days != null);

  const load = () => api.get("/patients").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/doctors").then((r) => setDoctors(r.data));
    api.get("/clinicians").then((r) => setClinicians(r.data.filter((c) => c.stage !== "inactive")));
  }, []);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus) openDetail({ _id: focus });
    if (searchParams.get("overdue") === "1") { setOverdueOnly(true); setView("list"); setListScope("active"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const move = async (item, stage) => {
    if (stage === "lost") { setLostFor(item); return; }
    await api.patch(`/patients/${item._id}/stage`, { stage });
    toast.success(`Moved to ${labelOf(PATIENT_STAGES, stage)}`);
    load();
  };

  const confirmLost = async (reason, note) => {
    await api.patch(`/patients/${lostFor._id}/stage`, { stage: "lost", lost_reason: reason, lost_note: note });
    toast.success(`Marked lost · ${labelOf(LOST_REASONS, reason)}`);
    setLostFor(null); load();
  };

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    const payload = { ...form, sessions_total: Number(form.sessions_total), revenue_per_session: Math.max(1000, Number(form.revenue_per_session) || 1000) };
    if (payload.source_type === "direct") payload.originating_doctor_id = null;
    else payload.direct_channel = "";
    if (!payload.originating_doctor_id) payload.originating_doctor_id = null;
    if (!payload.assigned_clinician_id) payload.assigned_clinician_id = null;
    await api.post("/patients", payload);
    toast.success("Patient added");
    setOpen(false); setForm(empty); load();
  };

  const openDetail = async (item) => {
    const { data } = await api.get(`/patients/${item._id}`);
    setDetail(data);
    setPEdit({ phone: data.phone || "", email: data.email || "", stage: data.stage, payment_status: data.payment_status, paid_date: data.paid_date || "", payment_method: data.payment_method || "", lost_reason: data.lost_reason || "", lost_note: data.lost_note || "", assigned_clinician_id: data.assigned_clinician_id || "", sessions_total: data.sessions_total || 0, sessions_attended: data.sessions_attended || 0, revenue_per_session: data.revenue_per_session || 1000 });
  };

  const savePatient = async () => {
    if (pedit.payment_status === "paid" && pedit.paid_date > todayLocal()) { toast.error("Paid date cannot be in the future"); return; }
    if (pedit.stage === "lost" && !pedit.lost_reason) { toast.error("Pick a reason for Lost"); return; }
    if (pedit.stage === "lost" && pedit.lost_reason === "other" && !pedit.lost_note.trim()) { toast.error("Describe the Other reason"); return; }
    const paid = pedit.payment_status === "paid";
    try {
      await api.patch(`/patients/${detail._id}`, { ...pedit, paid_date: paid ? (pedit.paid_date || todayLocal()) : null, payment_method: paid ? (pedit.payment_method || null) : null, assigned_clinician_id: pedit.assigned_clinician_id || null, sessions_total: Number(pedit.sessions_total) || 0, sessions_attended: Number(pedit.sessions_attended) || 0, revenue_per_session: Math.max(1000, Number(pedit.revenue_per_session) || 1000) });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); return; }
    toast.success("Patient updated");
    const { data } = await api.get(`/patients/${detail._id}`); setDetail(data); openDetail(data); load();
  };

  const assignClinician = async (clinicianId) => {
    await api.patch(`/patients/${detail._id}`, { assigned_clinician_id: clinicianId || null });
    toast.success(clinicianId ? "Clinician assigned" : "Clinician removed");
    const { data } = await api.get(`/patients/${detail._id}`); setDetail(data); load();
  };

  const markPaid = async (paid, date, method) => {
    await api.patch(`/patients/${detail._id}`, paid ? { payment_status: "paid", paid_date: date, payment_method: method } : { payment_status: "unpaid" });
    toast.success(paid ? `Marked paid on ${date}` : "Marked unpaid");
    const { data } = await api.get(`/patients/${detail._id}`); openDetail(data); load();
  };

  const markPaidRow = async (p, date, method) => {
    await api.patch(`/patients/${p._id}`, { payment_status: "paid", paid_date: date, payment_method: method });
    toast.success(`Marked paid on ${date}`);
    load();
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${detail.name}? This cannot be undone.`)) return;
    await api.delete(`/patients/${detail._id}`);
    toast.success("Patient deleted");
    setDetail(null); load();
  };

  const renderPatientCard = (p) => (
    <div>
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-sm text-ccc-navy leading-tight">{p.name}</p>
        {p.stage !== "lost" && <Badge className={`${PAY[p.payment_status]} border-0 text-[10px]`}>{p.payment_status}</Badge>}
      </div>
      {p.overdue_days != null && <p className="text-[10px] font-semibold text-ccc-amber mt-1" data-testid={`overdue-card-${p._id}`}>Overdue {p.overdue_days}d</p>}
      {p.stage === "lost" && p.lost_reason && <p className="text-[10px] text-slate-500 mt-1">{labelOf(LOST_REASONS, p.lost_reason)}</p>}
      {p.source_type === "clinician_order" ? (
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Stethoscope className="h-3 w-3 text-ccc-amber" />{p.originating_doctor_name || "Unassigned doctor"}</p>
      ) : (
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Megaphone className="h-3 w-3 text-ccc-blue" />Direct · {p.direct_channel}</p>
      )}
      {p.assigned_clinician_name && <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><HeartPulse className="h-3 w-3 text-ccc-teal" />{p.assigned_clinician_name}</p>}
      {p.sessions_total > 0 && (
        <div className="mt-2">
          <Progress value={(p.sessions_attended / p.sessions_total) * 100} className="h-1.5" />
          <p className="text-[10px] text-slate-400 mt-1">{p.sessions_attended}/{p.sessions_total} sessions</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy" data-testid="patients-title">Patient Onboarding</h1>
          <p className="text-sm text-slate-500 mt-1">End users · adjustable per-session revenue · linked to originating doctor</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <DataPorter entity="patients" onImported={load} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-patient-button" className="bg-ccc-blue hover:bg-ccc-blue2 text-white"><Plus className="h-4 w-4 mr-1.5" /> Add Patient</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Patient</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="patient-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Phone</Label><Input type="tel" data-testid="patient-phone-input" placeholder="98765 43210" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" data-testid="patient-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Source</Label>
                <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="patient-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clinician_order">Clinician Order (Doctor)</SelectItem>
                    <SelectItem value="direct">Direct / Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.source_type === "clinician_order" ? (
                <div><Label>Originating doctor</Label>
                  <Select value={form.originating_doctor_id} onValueChange={(v) => setForm({ ...form, originating_doctor_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="patient-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                    <SelectContent>{doctors.map((d) => <SelectItem key={d._id} value={d._id}>{d.name} · {d.city}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div><Label>Channel</Label>
                  <Select value={form.direct_channel} onValueChange={(v) => setForm({ ...form, direct_channel: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{DIRECT_CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Assigned clinician</Label>
                <Select value={form.assigned_clinician_id} onValueChange={(v) => setForm({ ...form, assigned_clinician_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="patient-clinician-select"><SelectValue placeholder="Select clinician" /></SelectTrigger>
                  <SelectContent>{clinicians.map((c) => <SelectItem key={c._id} value={c._id}>{c.name} · {c.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Total sessions</Label><Input type="number" value={form.sessions_total} onChange={(e) => setForm({ ...form, sessions_total: e.target.value })} className="mt-1" /></div>
              <div><Label>Revenue / session (₹, min 1000)</Label><Input type="number" min={1000} data-testid="patient-revenue-input" value={form.revenue_per_session} onChange={(e) => setForm({ ...form, revenue_per_session: e.target.value })} className="mt-1" /></div>
            </div>
            <DialogFooter><Button data-testid="patient-save-button" onClick={create} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {overdue.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="overdue-banner">
          <p className="text-sm text-amber-800 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /><b data-testid="overdue-count">{overdue.length}</b> enrolled patient{overdue.length > 1 ? "s" : ""} unpaid for {OVERDUE_DAYS}+ days · {inr(overdue.reduce((s, p) => s + (p.revenue_per_session || 0) * (p.sessions_total || 0), 0))} outstanding</p>
          <button data-testid="overdue-filter-toggle" onClick={() => { setOverdueOnly((v) => !v); setView("list"); setListScope("active"); }} className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${overdueOnly ? "bg-ccc-amber text-white" : "bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"}`}>{overdueOnly ? "Show all" : "Show overdue only"}</button>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <ViewToggle view={view} setView={setView} />
        {view === "list" && (
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1" data-testid="patient-scope-toggle">
            <button data-testid="scope-active" onClick={() => setListScope("active")} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${listScope === "active" ? "bg-ccc-navy text-white" : "text-slate-500 hover:text-ccc-navy"}`}>Active</button>
            <button data-testid="scope-closed" onClick={() => setListScope("closed")} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${listScope === "closed" ? "bg-ccc-navy text-white" : "text-slate-500 hover:text-ccc-navy"}`}>Lost &amp; Churned</button>
          </div>
        )}
      </div>
      <div className="mt-4" />
      {view === "list" && (
        <FunnelList
          items={items.filter((p) => (listScope === "closed" ? CLOSED_PATIENT_STAGES.includes(p.stage) : !CLOSED_PATIENT_STAGES.includes(p.stage)) && (!overdueOnly || p.overdue_days != null))}
          onRowClick={openDetail}
          testid="patients-list"
          rowAccent={(p) => PATIENT_STAGE_COLOR[p.stage] || "#94a3b8"}
          columns={[
            { label: "Patient ID", sortKey: "patient_code", render: (p) => p.patient_code ? <span className="font-mono text-xs font-semibold text-ccc-navy">{p.patient_code}</span> : <span className="text-xs text-slate-400">—</span> },
            { label: "Name", sortKey: "name", render: (p) => <span className="font-medium text-ccc-navy">{p.name}</span> },
            { label: "Source", render: (p) => p.source_type === "clinician_order" ? (p.originating_doctor_name || "Doctor order") : `Direct · ${p.direct_channel || "—"}` },
            { label: "Clinician", sortKey: "assigned_clinician_name", render: (p) => p.assigned_clinician_name || "—" },
            { label: "Revenue/session", sortKey: "revenue_per_session", render: (p) => inr(p.revenue_per_session || 0) },
            { label: "Payment", sortKey: "payment_status", render: (p) => p.stage === "lost"
              ? <span className="text-[10px] text-slate-400" data-testid={`lost-reason-row-${p._id}`}>{p.lost_reason ? labelOf(LOST_REASONS, p.lost_reason) : "n/a"}</span>
              : p.payment_status === "paid"
                ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]" title={p.paid_date ? `Paid on ${p.paid_date}` : undefined} data-testid={`paid-badge-${p._id}`}>paid{p.paid_date ? ` · ${p.paid_date}` : ""}{p.payment_method ? ` · ${labelOf(PAYMENT_METHODS, p.payment_method)}` : ""}</Badge>
                : <div className="flex items-center gap-1.5">{p.overdue_days != null && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]" data-testid={`overdue-badge-${p._id}`}>Overdue {p.overdue_days}d</Badge>}<MarkPaidPopover testId={`mark-paid-row-${p._id}`} onConfirm={(d, m) => markPaidRow(p, d, m)}><button data-testid={`mark-paid-row-${p._id}`} className="text-[10px] px-2 py-1 rounded-md bg-ccc-emerald text-white hover:bg-emerald-600 transition-colors">Mark paid</button></MarkPaidPopover></div> },
            { label: "Stage", sortKey: "stage", render: (p) => <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{labelOf(PATIENT_STAGES, p.stage)}</Badge> },
          ]}
        />
      )}
      {view === "board" && (
      <div className="space-y-8">
        <KanbanBoard
          stages={ACTIVE_PATIENT_STAGES}
          items={items.filter((p) => !CLOSED_PATIENT_STAGES.includes(p.stage))}
          accent="#2540FF"
          onMove={move}
          extraMoves={CLOSED_PATIENT_STAGE_DEFS}
          onCardClick={openDetail}
          cardAccent={(p) => PATIENT_STAGE_COLOR[p.stage] || "#94a3b8"}
          renderCard={renderPatientCard}
        />
        <div data-testid="patient-closed-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
            <h2 className="text-sm font-semibold text-slate-600 font-head uppercase tracking-wide">Lost &amp; Churned</h2>
            <span className="text-xs text-slate-400">({items.filter((p) => CLOSED_PATIENT_STAGES.includes(p.stage)).length})</span>
          </div>
          <KanbanBoard
            stages={CLOSED_PATIENT_STAGE_DEFS}
            items={items.filter((p) => CLOSED_PATIENT_STAGES.includes(p.stage))}
            accent="#f43f5e"
            onMove={move}
            extraMoves={ACTIVE_PATIENT_STAGES}
            onCardClick={openDetail}
            cardAccent={(p) => PATIENT_STAGE_COLOR[p.stage] || "#f43f5e"}
            renderCard={renderPatientCard}
          />
        </div>
      </div>
      )}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-head">{detail.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4" data-testid="patient-detail">
                <div className="flex gap-2">
                  <Badge className="bg-ccc-navy text-white border-0">{labelOf(PATIENT_STAGES, detail.stage)}</Badge>
                  {detail.stage !== "lost" && <Badge className={`${PAY[detail.payment_status]} border-0`}>{detail.payment_status}</Badge>}
                </div>
                <div data-testid="patient-id">
                  {detail.patient_code
                    ? <span className="font-mono text-sm font-semibold text-ccc-navy">Patient ID: {detail.patient_code}</span>
                    : <span className="text-xs text-slate-400">Patient ID is assigned on enrollment — this record is still at enquiry stage.</span>}
                </div>

                {pedit && (
                <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-3" data-testid="patient-edit">
                  <p className="text-xs font-semibold uppercase text-slate-400">Modify</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Phone</Label><Input type="tel" data-testid="edit-patient-phone" value={pedit.phone} onChange={(e) => setPEdit({ ...pedit, phone: e.target.value })} className="mt-1" /></div>
                    <div><Label>Email</Label><Input type="email" data-testid="edit-patient-email" value={pedit.email} onChange={(e) => setPEdit({ ...pedit, email: e.target.value })} className="mt-1" /></div>
                    <div><Label>Stage</Label><Select value={pedit.stage} onValueChange={(v) => setPEdit({ ...pedit, stage: v })}><SelectTrigger className="mt-1" data-testid="edit-patient-stage"><SelectValue /></SelectTrigger><SelectContent>{PATIENT_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select></div>
                    {pedit.stage !== "lost" && (
                    <div><Label>Payment</Label><Select value={pedit.payment_status} onValueChange={(v) => setPEdit({ ...pedit, payment_status: v, paid_date: v === "paid" ? (pedit.paid_date || detail.paid_date || todayLocal()) : "", payment_method: v === "paid" ? (pedit.payment_method || detail.payment_method || "upi") : "" })}><SelectTrigger className="mt-1" data-testid="edit-patient-payment-status"><SelectValue /></SelectTrigger><SelectContent>{["unpaid", "paid", "refunded"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    )}
                    {pedit.stage !== "lost" && pedit.payment_status === "paid" && (
                    <div className="col-span-2"><Label>Paid date</Label><DatePickerField testId="edit-patient-paid-date" value={pedit.paid_date || ""} onChange={(d) => setPEdit({ ...pedit, paid_date: d })} /></div>
                    )}
                    {pedit.stage !== "lost" && pedit.payment_status === "paid" && (
                    <div className="col-span-2"><Label>Paid via</Label><div className="mt-1"><MethodPicker value={pedit.payment_method} onChange={(m) => setPEdit({ ...pedit, payment_method: m })} testId="edit-patient-method" /></div></div>
                    )}
                    {pedit.stage === "lost" && (
                    <div className="col-span-2"><LostReasonFields prefix="edit-lost" reason={pedit.lost_reason} note={pedit.lost_note} onChange={({ reason, note }) => setPEdit({ ...pedit, lost_reason: reason, lost_note: note })} /></div>
                    )}
                    <div className="col-span-2"><Label>Assign clinician</Label><Select value={pedit.assigned_clinician_id || "none"} onValueChange={(v) => setPEdit({ ...pedit, assigned_clinician_id: v === "none" ? "" : v })}><SelectTrigger className="mt-1" data-testid="patient-assign-clinician"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">Unassigned</SelectItem>{clinicians.map((c) => <SelectItem key={c._id} value={c._id}>{c.name} · {c.city}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Sessions done</Label><Input type="number" value={pedit.sessions_attended} onChange={(e) => setPEdit({ ...pedit, sessions_attended: e.target.value })} className="mt-1" /></div>
                    <div><Label>Total sessions</Label><Input type="number" value={pedit.sessions_total} onChange={(e) => setPEdit({ ...pedit, sessions_total: e.target.value })} className="mt-1" /></div>
                    <div className="col-span-2"><Label>Revenue / session (₹, min 1000)</Label><Input type="number" min={1000} data-testid="edit-patient-revenue" value={pedit.revenue_per_session} onChange={(e) => setPEdit({ ...pedit, revenue_per_session: e.target.value })} className="mt-1" /></div>
                  </div>
                  <Button data-testid="save-patient-detail" onClick={savePatient} className="w-full bg-ccc-blue hover:bg-ccc-blue2 text-white">Save changes</Button>
                  {isAdmin && (
                    <Button data-testid="delete-patient-button" variant="outline" onClick={remove} className="w-full border-rose-300 text-ccc-rose hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-2" />Delete patient</Button>
                  )}
                </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3" data-testid="patient-originating-doctor">
                    <p className="text-[11px] uppercase text-ccc-amber font-semibold flex items-center gap-1"><Stethoscope className="h-3 w-3" /> Originating Doctor</p>
                    {detail.originating_doctor ? (
                      <>
                        <p className="text-sm font-semibold text-ccc-navy mt-1">{detail.originating_doctor.name}</p>
                        <p className="text-xs text-slate-500">{detail.originating_doctor.specialty} · {detail.originating_doctor.city}</p>
                      </>
                    ) : <p className="text-sm text-slate-500 mt-1">{detail.source_type === "clinician_order" ? "Doctor order · no doctor picked" : `Direct · ${detail.direct_channel || "—"}`}</p>}
                  </div>
                  <div className="bg-teal-50 border border-teal-100 rounded-xl p-3" data-testid="patient-assigned-clinician">
                    <p className="text-[11px] uppercase text-ccc-teal font-semibold flex items-center gap-1"><HeartPulse className="h-3 w-3" /> Assigned Clinician</p>
                    {detail.assigned_clinician ? (
                      <>
                        <p className="text-sm font-semibold text-ccc-navy mt-1">{detail.assigned_clinician.name}</p>
                        <p className="text-xs text-slate-500">{detail.assigned_clinician.city}</p>
                      </>
                    ) : <p className="text-sm text-slate-500 mt-1">Not assigned</p>}
                    <Select value={detail.assigned_clinician_id || "none"} onValueChange={(v) => assignClinician(v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-2 h-8 bg-white" data-testid="quick-assign-clinician"><SelectValue placeholder="Assign…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {clinicians.map((c) => <SelectItem key={c._id} value={c._id}>{c.name} · {c.city}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="patient-revenue">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase text-slate-400">Revenue</p>
                    {detail.stage !== "lost" && <Badge variant="outline" className="text-xs">{detail.payment_status}</Badge>}
                  </div>
                  <p className="text-sm text-ccc-navy mt-2">{inr(detail.revenue_per_session || 0) } / session × {detail.sessions_total || 0} sessions = <b>{inr((detail.revenue_per_session || 0) * (detail.sessions_total || 0))}</b></p>
                  {detail.stage === "lost" ? (
                    <p className="text-xs text-slate-400 mt-3" data-testid="lost-no-payment">No payment required — patient marked Lost{detail.lost_reason ? ` · ${labelOf(LOST_REASONS, detail.lost_reason)}` : ""}{detail.lost_note ? ` — ${detail.lost_note}` : ""}.</p>
                  ) : detail.payment_status === "paid" ? (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      {detail.paid_date && <span className="text-xs text-emerald-700" data-testid="patient-paid-date">Paid on {detail.paid_date}{detail.payment_method ? ` via ${labelOf(PAYMENT_METHODS, detail.payment_method)}` : ""}</span>}
                      <Button asChild size="sm" className="h-8 bg-ccc-navy hover:bg-ccc-blue text-white"><a href={`/receipt/${detail._id}`} target="_blank" rel="noreferrer" data-testid="view-receipt-button"><ReceiptIcon className="h-3.5 w-3.5 mr-1.5" />Receipt</a></Button>
                      <Button data-testid="mark-unpaid-button" variant="outline" size="sm" onClick={() => markPaid(false)} className="h-8 border-slate-300 text-slate-600">Mark unpaid</Button>
                    </div>
                  ) : (
                    <>
                    {detail.overdue_days != null && <p className="text-xs font-semibold text-ccc-amber mt-2" data-testid="patient-overdue">Overdue — enrolled {detail.overdue_days} days ago, still unpaid</p>}
                    <MarkPaidPopover testId="mark-paid" onConfirm={(d, m) => markPaid(true, d, m)}>
                      <Button data-testid="mark-paid-button" size="sm" className="mt-3 h-8 bg-ccc-emerald hover:bg-emerald-600 text-white">Mark paid — collect {inr((detail.revenue_per_session || 0) * (detail.sessions_total || 0))}</Button>
                    </MarkPaidPopover>
                    <PaymentReminder patient={detail} onSent={() => openDetail(detail)} />
                    </>
                  )}
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-1">Session Adherence</p>
                  <div className="flex items-center gap-3 mt-1">
                    <Progress value={detail.sessions_total ? (detail.sessions_attended / detail.sessions_total) * 100 : 0} className="flex-1 h-2.5" />
                    <span className="text-sm font-semibold text-ccc-navy">{detail.sessions_attended}/{detail.sessions_total}</span>
                  </div>
                  {detail.outcome && <p className="text-xs text-slate-500 mt-2">Outcome: <b className="text-ccc-emerald">{detail.outcome}</b></p>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <LostReasonDialog patient={lostFor} onConfirm={confirmLost} onCancel={() => setLostFor(null)} />
    </div>
  );
}
